<!-- components/SearchSelect.vue -->
  <template>
    <div class="search-select">
      <input
        type="text"
        :value="searchQuery"
        @input="handleInput"
        @blur="closeResults"
        @focus="showResults"
        :placeholder="placeholder"
        class="form-control"
        :disabled="disabled"
        ref="inputElement"
      />
      
      <div v-if="loading" class="loading">Buscando...</div>
      
      <ul v-if="showResultsList && items.length > 0" class="results-list">
        <li
          v-for="item in items"
          :key="item.id"
          @mousedown="selectItem(item)"
          class="result-item"
        >
          {{ item[itemText] }}
        </li>
      </ul>
    </div>
  </template>
  
  <script setup lang="ts">
  import { ref, watch, nextTick, onMounted } from 'vue';
  
  interface Item {
    id: number | string;
    [key: string]: any;
  }

  const inputElement = ref<HTMLInputElement | null>(null)

  const props = defineProps({
    items: {
      type: Array as () => Item[],
      default: () => []
    },
    loading: Boolean,
    itemText: {
      type: String,
      default: 'name'
    },
    placeholder: String,
    resetTrigger: Boolean,
    disabled: Boolean,
    initialValue: {
      type: String,
      default: "",
    },
  });

  const emit = defineEmits(['search', 'select']);
  
  const searchQuery = ref('');
  const showResultsList = ref(false);
  
  const handleInput = (event: Event) => {
    const query = (event.target as HTMLInputElement).value;
    searchQuery.value = query;
    showResultsList.value = query.length > 0;
    emit('search', query);
  };

  // Nuevo método para resetear el input
  const resetInput = () => {
    searchQuery.value = '';
    if (inputElement.value) {
      inputElement.value.value = '';
    }
  };
  
  // Exponer métodos públicos
  defineExpose({
    resetInput
  });

  const selectItem = (item: any) => {
    searchQuery.value = item[props.itemText];
    showResultsList.value = false;
    emit('select', item);
  };
  
  const closeResults = () => {
    setTimeout(() => {
      showResultsList.value = false;
    }, 200);
  };
  
  const showResults = () => {
    if (searchQuery.value) {
      showResultsList.value = true;
    }
  };

  watch(() => props.resetTrigger, () => {
    searchQuery.value = '';
    if (inputElement.value) {
      inputElement.value.value = '';
    }
  });

  // Cargar valor inicial al montar
  onMounted(() => {
    if (props.initialValue) {
      searchQuery.value = props.initialValue;
      if (inputElement.value) {
        inputElement.value.value = props.initialValue;
      }
    }
  });

  // Observar cambios en initialValue
  watch(() => props.initialValue, (newValue) => {
    if (newValue !== null && newValue !== undefined) {
      searchQuery.value = newValue;
      if (inputElement.value) {
        inputElement.value.value = newValue;
      }
    }
  }, { immediate: true }); // Añadir immediate: true
  </script>

<style scoped>
.search-select[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}

.text-muted {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
  display: block;
}
</style>
